import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import useFetch from "../../../clients/features/get_withcredentials";
import {
  TbCalendar, TbList, TbTable,
  TbChevronLeft, TbChevronRight,
  TbSearch, TbChevronUp, TbChevronDown, TbSelector,
  TbCheck, TbX, TbAlertTriangle, TbEye, TbClock,
  TbArrowRight, TbArrowNarrowRight,
  TbUsers, TbWorld, TbBuildingStore,
  TbCalendarEvent, TbMinus, TbPlus, TbEqual, TbInfoCircle,
  TbCurrencyEuro, TbMail, TbPhone
} from "react-icons/tb";

const API = "https://api.komorotel.com/api";

// ── Utils ─────────────────────────────────────────────────────────
const normalize = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const nights = (a, b) =>
  Math.ceil((new Date(b) - new Date(a)) / 86400000);

const fmtShort = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const fmtLong = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

// 🆕 VERSEMENT — échéance de règlement : l'heure fait partie du délai.
const fmtDeadline = (d) =>
  new Date(d).toLocaleString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

// ✅ MODIFICATION — les dates sont stockées à minuit UTC : on découpe l'ISO
//    plutôt que de passer par le fuseau local, qui décalerait d'un jour.
const toInputDate = (d) => new Date(d).toISOString().slice(0, 10);

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/* ═══════════════════════════════════════════════════════════
   🆕 PÉRIODES

   Une réservation sur forfait porte periods et unitNights. Elle se décrit
   en "2 mois", pas en "60 nuits" : c'est l'unité dans laquelle elle a été
   vendue, et celle que l'hébergeur retrouve dans sa grille tarifaire.
   ═══════════════════════════════════════════════════════════ */
const UNIT_LABEL = { 7: "semaine", 30: "mois" };

const isPeriodRes = (r) => Boolean(r?.periods && r?.unitNights);

const unitPlural = (unitNights, n) => {
  const l = UNIT_LABEL[unitNights] || "période";
  if (l === "mois") return l;
  return n > 1 ? `${l}s` : l;
};

const stayLabel = (r) => {
  if (isPeriodRes(r)) return `${r.periods} ${unitPlural(r.unitNights, r.periods)}`;
  const n = nights(r.checkInDate, r.checkOutDate);
  return `${n} nuit${n > 1 ? "s" : ""}`;
};

const MEAL_LABELS = {
  none: null,
  breakfast: "🍳 Petit-déjeuner",
  half_board: "🍽️ Demi-pension",
  full_board: "🍱 Pension complète"
};

// ── No-show ───────────────────────────────────────────────────────
// Fenêtre de signalement : du jour d'arrivée à 48h après le check-in.
// Un no-show se constate à l'arrivée, pas à la fin du séjour : borner sur
// le check-in donne la même durée pour tous les séjours et libère vite les
// nuits restantes. Au-delà, l'hébergeur passe par le support.
//
// "completed" reste accepté : sur une nuit unique, le cron passe la
// réservation en terminé dès le lendemain, avant la fin de la fenêtre.
const NO_SHOW_WINDOW_MS = 48 * 3600000;

const noShowDeadline = (r) =>
  new Date(new Date(r.checkInDate).getTime() + NO_SHOW_WINDOW_MS);

const canReportNoShow = (r) =>
  ["confirmed", "completed"].includes(r.status) &&
  normalize(new Date()) >= normalize(r.checkInDate) &&
  new Date() <= noShowDeadline(r);

// ── Couleurs par chambre ──────────────────────────────────────────
const ROOM_COLORS = [
  { bg: "bg-blue-100",    text: "text-blue-700",    bar: "bg-blue-400" },
  { bg: "bg-violet-100",  text: "text-violet-700",  bar: "bg-violet-400" },
  { bg: "bg-amber-100",   text: "text-amber-700",   bar: "bg-amber-400" },
  { bg: "bg-rose-100",    text: "text-rose-700",    bar: "bg-rose-400" },
  { bg: "bg-cyan-100",    text: "text-cyan-700",    bar: "bg-cyan-400" },
  { bg: "bg-emerald-100", text: "text-emerald-700", bar: "bg-emerald-400" },
];

const roomColor = (title = "") => {
  const i = title.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % ROOM_COLORS.length;
  return ROOM_COLORS[i];
};

// ── Status config ─────────────────────────────────────────────────
const STATUS = {
  confirmed:      { label: "Confirmée",          dot: "bg-emerald-400", badge: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  completed:      { label: "Terminée",            dot: "bg-slate-300",   badge: "bg-slate-50 text-slate-500 ring-slate-200" },
  pending:        { label: "En attente",          dot: "bg-amber-400",   badge: "bg-amber-50 text-amber-700 ring-amber-200" },
  pending_cancel: { label: "Annulation demandée", dot: "bg-orange-400",  badge: "bg-orange-50 text-orange-700 ring-orange-200" },
  cancelled:      { label: "Annulée",             dot: "bg-red-400",     badge: "bg-red-50 text-red-600 ring-red-200" },
  no_show:        { label: "No-show",             dot: "bg-slate-400",   badge: "bg-slate-100 text-slate-600 ring-slate-200" },
  // 🆕 VERSEMENT — les nuits sont bloquées, le séjour ne l'est pas encore.
  // Le libellé dit l'action attendue, pas l'état : "En attente" laisserait
  // croire que l'hébergeur n'a rien à faire.
  awaiting_deposit: { label: "Règlement à valider", dot: "bg-red-400", badge: "bg-red-50 text-red-700 ring-red-200" },
};

const getStatus = (s) => STATUS[s] || { label: s, dot: "bg-gray-300", badge: "bg-gray-100 text-gray-500 ring-gray-200" };

const StatusBadge = ({ status }) => {
  const s = getStatus(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${s.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ── Helpers ───────────────────────────────────────────────────────
const formatGuests = (guests) => {
  if (!guests) return null;
  const parts = [];
  if (guests.adults > 0) parts.push(`${guests.adults} adulte${guests.adults > 1 ? "s" : ""}`);
  if (guests.children > 0) parts.push(`${guests.children} enfant${guests.children > 1 ? "s" : ""}`);
  return parts.join(" · ") || null;
};

const extractError = (err) =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || "Une erreur est survenue.";

/* ══════════════════════════════════════════════════════════════════
   🆕 VERSEMENT — validation de l'encaissement

   Komorotel ne voit aucun paiement : c'est l'hébergeur qui constate le
   règlement et qui le déclare. Cette action confirme le séjour, envoie la
   confirmation au client et pousse la réservation vers le PMS.

   Les nuits sont DÉJÀ bloquées depuis la demande : valider ne prend rien
   de plus dans le calendrier. À l'inverse, ne rien faire avant l'échéance
   les rend automatiquement à la vente.
══════════════════════════════════════════════════════════════════ */
function DepositDialog({ reservation, onClose, onDone }) {
  const [reference, setReference] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const r = reservation;
  const deposit = r.deposit || {};
  const rest = Math.round(((r.totalPrice || 0) - (deposit.amount || 0)) * 100) / 100;
  const overdue = deposit.dueAt && new Date(deposit.dueAt) < new Date();

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/reservation/${r._id}/deposit-paid`,
        { reference: reference || undefined },
        { withCredentials: true }
      );
      onDone();
    } catch (err) {
      setError(extractError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Valider le règlement</h3>
          <p className="text-sm text-gray-400 mt-0.5">
            {r.guest?.firstName} {r.guest?.lastName} · {stayLabel(r)} dès le {fmtShort(r.checkInDate)}
          </p>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">

          {/* Montant attendu */}
          <div className="px-4 py-3.5 bg-gray-50 rounded-xl">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-gray-400">Montant attendu</span>
              <span className="text-2xl font-bold text-gray-900">
                {deposit.amount?.toLocaleString("fr-FR")} €
              </span>
            </div>
            {deposit.amountKMF && (
              <p className="text-right text-xs text-gray-400 mt-0.5">
                {deposit.amountKMF.toLocaleString("fr-FR")} KMF
              </p>
            )}
            {rest > 0 && (
              <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200">
                Solde de {rest.toLocaleString("fr-FR")} € à percevoir sur place
                (total du séjour : {r.totalPrice?.toLocaleString("fr-FR")} €).
              </p>
            )}
          </div>

          {/* Échéance */}
          {deposit.dueAt && (
            <div className={`px-4 py-3 rounded-xl border ${overdue ? "bg-red-50 border-red-100" : "bg-amber-50 border-amber-100"}`}>
              <p className={`text-xs ${overdue ? "text-red-700" : "text-amber-800"}`}>
                {overdue
                  // L'échéance dépassée n'empêche pas de valider tant que le
                  // job n'est pas passé : un paiement reçu en retard reste un
                  // paiement reçu.
                  ? `Échéance dépassée depuis le ${fmtDeadline(deposit.dueAt)}. Vous pouvez encore valider tant que la réservation n'a pas été annulée automatiquement.`
                  : `À valider avant le ${fmtDeadline(deposit.dueAt)}, sinon les dates repartent automatiquement à la vente.`}
              </p>
            </div>
          )}

          {/* Contact client */}
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold text-gray-500">Contact du client</p>
            {r.guest?.email && (
              <a href={`mailto:${r.guest.email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
                <TbMail className="text-gray-400 shrink-0" /> {r.guest.email}
              </a>
            )}
            {r.guest?.phone && (
              <a href={`tel:${r.guest.phone}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
                <TbPhone className="text-gray-400 shrink-0" /> {r.guest.phone}
              </a>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Référence du paiement <span className="font-normal text-gray-400">(facultatif)</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="N° de virement, reçu, espèces…"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
            />
            <p className="text-[11px] text-gray-400 mt-1.5">
              Conservée sur la réservation, utile en cas de contestation.
            </p>
          </div>

          <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
            <p className="text-xs text-blue-700">
              En validant, la réservation passe en confirmée et le client reçoit sa
              confirmation. Les dates sont déjà bloquées dans votre calendrier.
            </p>
          </div>

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2.5">
              <TbAlertTriangle className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[var(--k-green)] text-white hover:opacity-90 transition disabled:opacity-40"
          >
            {saving ? "Validation…" : "J'ai reçu le règlement"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MODIFICATION DE RÉSERVATION — drawer latéral

   Deux temps, volontairement séparés :
   1. Aperçu    → PATCH /reservation/:id/modification/preview  (lecture seule)
   2. Appliquer → PATCH /reservation/:id/modification          (écriture)

   L'aperçu renvoie un token signé qui scelle les dates ET le prix affiché.
   Si le tarif bouge entre les deux, le backend refuse : impossible
   d'appliquer un montant que l'hôtelier n'a pas vu à l'écran.

   🆕 PÉRIODES — sur un séjour forfaitaire, on ne choisit pas une date de
   sortie mais un nombre de périodes. La sortie est calculée par le serveur.
══════════════════════════════════════════════════════════════════ */
function ModificationDrawer({ reservation, onClose, onDone }) {
  const period = isPeriodRes(reservation);   // 🆕 PÉRIODES

  const [checkIn, setCheckIn]   = useState(toInputDate(reservation.checkInDate));
  const [checkOut, setCheckOut] = useState(toInputDate(reservation.checkOutDate));
  const [periods, setPeriods]   = useState(reservation.periods || 1);
  const [reason, setReason]     = useState("");

  const [preview, setPreview]   = useState(null);
  const [error, setError]       = useState(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const today = toInputDate(new Date());

  // 🆕 PÉRIODES — sortie calculée côté client pour l'affichage ; le serveur
  // reste la source de vérité et la renvoie dans l'aperçu.
  const computedCheckOut = period
    ? toInputDate(addDays(new Date(checkIn), periods * reservation.unitNights))
    : checkOut;

  const unchanged = period
    ? (checkIn === toInputDate(reservation.checkInDate) && periods === reservation.periods)
    : (checkIn === toInputDate(reservation.checkInDate) && checkOut === toInputDate(reservation.checkOutDate));

  const datesValid = period
    ? (checkIn && checkIn >= today && periods >= 1)
    : (checkIn && checkOut && checkOut > checkIn && checkIn >= today);

  // Toute retouche invalide l'aperçu : le token ne correspond plus.
  useEffect(() => {
    setPreview(null);
    setError(null);
  }, [checkIn, checkOut, periods]);

  // Compte à rebours de validité de l'aperçu (15 min côté serveur).
  useEffect(() => {
    if (!preview?.expiresAt) return;
    const tick = () => setRemaining(Math.max(0, Math.floor((preview.expiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [preview]);

  // 🆕 PÉRIODES — le corps envoyé décrit le séjour dans son unité.
  const buildBody = () => period
    ? { checkInDate: checkIn, periods }
    : { checkInDate: checkIn, checkOutDate: checkOut };

  const handlePreview = async () => {
    setChecking(true);
    setError(null);
    try {
      const { data } = await axios.patch(
        `${API}/reservation/${reservation._id}/modification/preview`,
        buildBody(),
        { withCredentials: true }
      );
      setPreview(data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setChecking(false);
    }
  };

  const handleApply = async () => {
    if (!preview) return;
    setApplying(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/reservation/${reservation._id}/modification`,
        {
          ...buildBody(),
          token: preview.token,
          expiresAt: preview.expiresAt,
          reason: reason || undefined,
        },
        { withCredentials: true }
      );
      onDone();
    } catch (err) {
      setError(extractError(err));
      setApplying(false);
    }
  };

  const delta = preview?.delta ?? 0;
  const expired = preview && remaining <= 0;
  const noneLeft = preview?.modificationsLeft === 0;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={onClose} />

      <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col animate-[slideIn_.2s_ease-out]">
        <div className="h-0.5 w-full bg-gradient-to-r from-[var(--k-green)] via-[#5abf3a] to-[rgb(var(--k-green-rgb)/0.3)]" />

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {period ? "Modifier le séjour" : "Modifier les dates"}
            </h3>
            <p className="text-sm text-gray-400 mt-0.5">
              {reservation.guest?.firstName} {reservation.guest?.lastName} · {reservation.roomId?.title}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 transition text-gray-400">
            <TbX />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {/* Séjour actuel */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-xs text-gray-400 mb-1.5">Séjour actuel</p>
            <p className="text-sm font-semibold text-gray-800">
              {fmtLong(reservation.checkInDate)} → {fmtLong(reservation.checkOutDate)}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {stayLabel(reservation)}
              {period && ` · ${nights(reservation.checkInDate, reservation.checkOutDate)} nuits`} ·{" "}
              {reservation.totalPrice?.toLocaleString("fr-FR")} €
            </p>
          </div>

          {/* 🆕 PÉRIODES — arrivée + nombre de périodes, sortie calculée */}
          {period ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Nouvelle arrivée</label>
                  <input
                    type="date"
                    value={checkIn}
                    min={today}
                    onChange={(e) => setCheckIn(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                    Nombre de {unitPlural(reservation.unitNights, 2)}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={periods}
                    onChange={(e) => setPeriods(Math.max(1, Number(e.target.value) || 1))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
                  />
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 rounded-xl flex items-start gap-2.5">
                <TbInfoCircle className="text-gray-400 shrink-0 mt-0.5" />
                <p className="text-xs text-gray-500 leading-relaxed">
                  Départ calculé : <span className="font-semibold text-gray-700">{fmtLong(computedCheckOut)}</span>
                  {" "}({periods * reservation.unitNights} nuits). Un forfait se vend par{" "}
                  {unitPlural(reservation.unitNights, 1)} entière{reservation.unitNights === 30 ? "" : "s"},
                  la date de sortie ne se choisit pas.
                </p>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Nouvelle arrivée</label>
                <input
                  type="date"
                  value={checkIn}
                  min={today}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Nouveau départ</label>
                <input
                  type="date"
                  value={checkOut}
                  min={checkIn || today}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Motif <span className="font-normal text-gray-400">(facultatif)</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Vol reporté, accord avec le client…"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
            />
          </div>

          {!preview && (
            <button
              onClick={handlePreview}
              disabled={!datesValid || unchanged || checking}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {checking ? "Vérification…" : "Vérifier la disponibilité"}
            </button>
          )}

          {unchanged && !preview && (
            <p className="text-xs text-gray-400 text-center -mt-2">
              {period
                ? "Changez la date d'arrivée ou la durée pour continuer."
                : "Choisissez des dates différentes pour continuer."}
            </p>
          )}

          {/* Erreur */}
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2.5">
              <TbAlertTriangle className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* ── APERÇU ─────────────────────────────────────── */}
          {preview && (
            <div className="flex flex-col gap-4 pt-1">

              {/* Disponibilité */}
              {preview.available ? (
                <div className="px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-2.5">
                  <TbCheck className="text-emerald-600 shrink-0" />
                  <p className="text-sm text-emerald-700 font-medium">Les nouvelles nuits sont disponibles</p>
                </div>
              ) : (
                <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-700 font-semibold mb-1.5">Nuits indisponibles</p>
                  <ul className="flex flex-col gap-0.5">
                    {preview.blocked.map((b, i) => (
                      <li key={i} className="text-xs text-red-600">
                        {fmtLong(b.date)} — {b.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Détail des nuits */}
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500">Nuits concernées</p>
                </div>
                <div className="p-4 flex flex-col gap-2.5">
                  {[
                    { icon: <TbMinus />, label: "Libérées", list: preview.nights.released, cls: "bg-amber-50 text-amber-700" },
                    { icon: <TbPlus />,  label: "Réservées", list: preview.nights.booked,   cls: "bg-blue-50 text-blue-700" },
                    { icon: <TbEqual />, label: "Inchangées", list: preview.nights.unchanged, cls: "bg-gray-100 text-gray-500" },
                  ].filter(g => g.list.length > 0).map((g, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-xs text-gray-400 w-20 shrink-0 flex items-center gap-1 pt-1">
                        {g.icon} {g.label}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {g.list.map(d => (
                          <span key={d} className={`px-1.5 py-0.5 rounded-md text-[11px] font-medium ${g.cls}`}>
                            {fmtShort(d)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {preview.nights.unchanged.length > 0 && (
                    <p className="text-[11px] text-gray-400 flex items-start gap-1.5 pt-1 border-t border-gray-50 mt-1">
                      <TbInfoCircle className="shrink-0 mt-0.5" />
                      Les nuits déjà occupées par cette réservation ne sont pas retouchées.
                    </p>
                  )}
                </div>
              </div>

              {/* Prix */}
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500">Montants</p>
                </div>
                <div className="p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Avant</span>
                    <span className="text-gray-500">
                      {/* 🆕 PÉRIODES — l'aperçu renvoie periods quand il y a lieu */}
                      {preview.current.periods
                        ? `${preview.current.periods} ${unitPlural(reservation.unitNights, preview.current.periods)}`
                        : `${preview.current.totalNights} nuits`} · {preview.current.totalPrice?.toLocaleString("fr-FR")} €
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Après</span>
                    <span className="font-semibold text-gray-900">
                      {preview.proposed.periods
                        ? `${preview.proposed.periods} ${unitPlural(preview.proposed.unitNights, preview.proposed.periods)}`
                        : `${preview.proposed.totalNights} nuits`} · {preview.proposed.totalPrice?.toLocaleString("fr-FR")} €
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-50">
                    <span className="text-sm text-gray-500">Différence</span>
                    <span className={`text-lg font-bold ${
                      delta > 0 ? "text-emerald-600" : delta < 0 ? "text-orange-500" : "text-gray-400"
                    }`}>
                      {delta > 0 ? "+" : ""}{delta.toLocaleString("fr-FR")} €
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs pt-1">
                    <span className="text-gray-400">Commission Komorotel</span>
                    <span className="text-gray-500">
                      {preview.current.platformFee?.toLocaleString("fr-FR")} €
                      <TbArrowNarrowRight className="inline mx-1 text-gray-300" />
                      <span className="font-semibold text-orange-500">
                        {preview.proposed.platformFee?.toLocaleString("fr-FR")} €
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Validité de l'aperçu */}
              {!expired ? (
                <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1.5">
                  <TbClock /> Devis valable encore {Math.floor(remaining / 60)} min {remaining % 60}s
                </p>
              ) : (
                <div className="px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
                  <p className="text-sm text-amber-700">Le devis a expiré. Relancez la vérification.</p>
                </div>
              )}

              {noneLeft && (
                <div className="px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
                  <p className="text-sm text-amber-700">
                    Cette réservation a atteint son nombre maximum de modifications.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {preview && (
          <div className="px-6 py-4 border-t border-gray-100 flex gap-2">
            <button
              onClick={handlePreview}
              disabled={checking || applying}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition disabled:opacity-50"
            >
              {checking ? "…" : "Revérifier"}
            </button>
            <button
              onClick={handleApply}
              disabled={!preview.available || expired || noneLeft || applying}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[var(--k-green)] text-white hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {applying ? "Application…" : "Appliquer la modification"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════
   NON-PRÉSENTATION — dialogue de confirmation

   Le signalement n'est pas qu'un changement de statut : le montant
   éventuellement retenu au client détermine la commission Komorotel.
   Aucun frais retenu = aucune commission. On demande donc le montant
   plutôt que de le supposer.
══════════════════════════════════════════════════════════════════ */
function NoShowDialog({ reservation, onClose, onDone }) {
  const [charge, setCharge] = useState(false);
  const [fee, setFee]       = useState("");
  const [error, setError]   = useState(null);
  const [saving, setSaving] = useState(false);

  const r = reservation;
  const maxFee = r.totalPrice || 0;
  const feeValue = charge ? Math.min(parseFloat(fee) || 0, maxFee) : 0;
  const commission = r.source === "site" ? Math.round(feeValue * 0.08 * 100) / 100 : 0;

  const invalid = charge && (!fee || parseFloat(fee) <= 0);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(
        `${API}/reservation/no-show/${r._id}`,
        { noShowFee: feeValue },
        { withCredentials: true }
      );
      onDone();
    } catch (err) {
      setError(extractError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Signaler une non-présentation</h3>
          <p className="text-sm text-gray-400 mt-0.5">
            {r.guest?.firstName} {r.guest?.lastName} · {fmtShort(r.checkInDate)} → {fmtShort(r.checkOutDate)}
          </p>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">

          <div className="px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
            <p className="text-xs text-amber-800">
              Avez-vous tenté de joindre le client ? Cette action annule la réservation
              et libère les nuits restantes à la vente.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Frais retenus au client</p>
            <div className="flex flex-col gap-1.5">
              {[
                { v: false, label: "Aucun frais", sub: "Aucune commission Komorotel" },
                { v: true,  label: "Frais retenus selon mes conditions", sub: "Commission calculée sur ce montant" },
              ].map((opt) => (
                <button
                  key={String(opt.v)}
                  onClick={() => setCharge(opt.v)}
                  className={`text-left px-3.5 py-2.5 rounded-xl border transition ${
                    charge === opt.v
                      ? "border-gray-900 bg-gray-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{opt.sub}</p>
                </button>
              ))}
            </div>
          </div>

          {charge && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                Montant retenu (max {maxFee.toLocaleString("fr-FR")} €)
              </label>
              <input
                type="number"
                min="0"
                max={maxFee}
                step="0.01"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
              />
              {r.source === "site" && feeValue > 0 && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Commission Komorotel :{" "}
                  <span className="font-semibold text-orange-500">
                    {commission.toLocaleString("fr-FR")} €
                  </span>
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2.5">
              <TbAlertTriangle className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={invalid || saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-slate-800 text-white hover:bg-slate-900 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Enregistrement…" : "Confirmer la non-présentation"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal détail ──────────────────────────────────────────────────
function ReservationModal({ r, onClose, onNoShow, onCancelApprove, onModify, onDeposit, cancelReq, loading }) {
  if (!r) return null;
  const today = normalize(new Date());
  const ci = normalize(r.checkInDate);
  const co = normalize(r.checkOutDate);
  const isPast     = today >= co;
  const isOngoing  = today >= ci && today < co;
  const isUpcoming = today < ci;
  const n = nights(r.checkInDate, r.checkOutDate);
  const meal = MEAL_LABELS[r.ratePlanSnapshot?.mealPlan];
  const guestsLabel = formatGuests(r.guests);

  // 🆕 VERSEMENT
  const awaitingDeposit = r.status === "awaiting_deposit";
  const period = isPeriodRes(r);   // 🆕 PÉRIODES

  // ✅ MODIFICATION — uniquement avant l'arrivée.
  //    Une fois le client sur place, l'arrangement se fait directement
  //    avec l'établissement : ce n'est plus une modification de plateforme.
  const canModify = r.status === "confirmed" && isUpcoming;

  // ✅ NO-SHOW — voir canReportNoShow en haut du fichier.
  const canMarkNoShow = canReportNoShow(r);

  // Délai passé : on l'indique au lieu de laisser la fiche muette.
  const noShowExpired =
    !canMarkNoShow &&
    ["confirmed", "completed"].includes(r.status) &&
    today >= ci &&
    new Date() > noShowDeadline(r);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Accent bar */}
        <div className="h-0.5 w-full bg-gradient-to-r from-[var(--k-green)] via-[#5abf3a] to-[rgb(var(--k-green-rgb)/0.3)]" />

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-lg">
                {r.reservationId}
              </span>
              {r.source === "admin" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-purple-50 text-purple-600 text-[11px] font-semibold">
                  <TbBuildingStore className="text-xs" /> Admin
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-semibold">
                  <TbWorld className="text-xs" /> Site web
                </span>
              )}
              {/* 🆕 PÉRIODES — un séjour forfaitaire se signale ici */}
              {period && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[rgb(var(--k-green-rgb)/0.08)] text-[var(--k-green)] text-[11px] font-semibold">
                  <TbCalendar className="text-xs" /> {stayLabel(r)}
                </span>
              )}
            </div>
            <h3 className="text-lg font-bold text-gray-900">{r.guest?.firstName} {r.guest?.lastName}</h3>
            <div className="mt-1.5"><StatusBadge status={r.status} /></div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 transition text-gray-400">
            <TbX />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto max-h-[60vh]">

          {/* 🆕 VERSEMENT — en tête de fiche : c'est l'information qui décide
              de ce que l'hébergeur doit faire maintenant. */}
          {awaitingDeposit && r.deposit && (
            <div className="mb-5 px-4 py-3.5 bg-red-50 border border-red-100 rounded-xl">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs font-semibold text-red-800">Règlement attendu</span>
                <span className="text-xl font-bold text-red-800">
                  {r.deposit.amount?.toLocaleString("fr-FR")} €
                </span>
              </div>
              {r.deposit.dueAt && (
                <p className="text-xs text-red-600">
                  Avant le {fmtDeadline(r.deposit.dueAt)} — sinon les dates repartent à la vente
                </p>
              )}
              <p className="text-[11px] text-red-500 mt-2 pt-2 border-t border-red-100">
                Les nuits sont déjà bloquées dans votre calendrier.
              </p>
            </div>
          )}

          {/* Dates highlight */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-5">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{new Date(r.checkInDate).getDate()}</p>
              <p className="text-xs text-gray-400">{new Date(r.checkInDate).toLocaleString("fr-FR", { month: "short" })}</p>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 h-px bg-gray-300" />
              {/* 🆕 PÉRIODES — durée dans l'unité vendue */}
              <span className="text-xs text-gray-400 shrink-0">{stayLabel(r)}</span>
              <div className="flex-1 h-px bg-gray-300" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{new Date(r.checkOutDate).getDate()}</p>
              <p className="text-xs text-gray-400">{new Date(r.checkOutDate).toLocaleString("fr-FR", { month: "short" })}</p>
            </div>
          </div>

          {/* Infos */}
          <div className="flex flex-col gap-0">
            {[
              { label: "Chambre",    value: r.roomId?.title || "—" },
              { label: "Tarif",      value: r.ratePlanSnapshot?.name || "Standard" },
              ...(period ? [{ label: "Durée", value: `${stayLabel(r)} · ${n} nuits` }] : []),
              ...(meal ? [{ label: "Repas", value: meal }] : []),
              ...(guestsLabel ? [{ label: "Voyageurs", value: guestsLabel }] : []),
              { label: "Email",      value: r.guest?.email || "—" },
              { label: "Tél",        value: r.guest?.phone || "—" },
              // 🆕 VERSEMENT — trace du règlement une fois validé
              ...(r.deposit?.paidAt ? [{ label: "Règlement reçu", value: fmtShort(r.deposit.paidAt) + (r.deposit.reference ? ` · ${r.deposit.reference}` : "") }] : []),
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-400">{item.label}</span>
                <span className="text-sm font-medium text-gray-800 text-right max-w-[60%] truncate">{item.value}</span>
              </div>
            ))}
          </div>

          {/* ✅ MODIFICATION — historique */}
          {r.modificationCount > 0 && (
            <div className="mt-4 px-4 py-3 bg-gray-50 rounded-xl">
              <p className="text-xs font-semibold text-gray-500 mb-2">
                {r.modificationCount} modification{r.modificationCount > 1 ? "s" : ""}
              </p>
              <div className="flex flex-col gap-1.5">
                {(r.modifications || []).map((m, i) => (
                  <div key={i} className="text-xs text-gray-400">
                    {fmtShort(m.from?.checkInDate)} → {fmtShort(m.from?.checkOutDate)}
                    <TbArrowNarrowRight className="inline mx-1" />
                    <span className="text-gray-600 font-medium">
                      {fmtShort(m.to?.checkInDate)} → {fmtShort(m.to?.checkOutDate)}
                    </span>
                    {m.reason && <span className="ml-1 italic">· {m.reason}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prix */}
          <div className="flex items-end justify-between pt-4 mt-2 border-t border-gray-100">
            <span className="text-sm text-gray-500">Total</span>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
              {r.totalPriceKMF && (
                <p className="text-xs text-gray-400">{r.totalPriceKMF?.toLocaleString("fr-FR")} KMF</p>
              )}
            </div>
          </div>

          {isOngoing && !awaitingDeposit && (
            <div className="mt-4 px-4 py-2.5 bg-emerald-50 rounded-xl text-sm text-emerald-700 font-medium flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Séjour en cours
            </div>
          )}
          {isUpcoming && !awaitingDeposit && (
            <div className="mt-4 px-4 py-2.5 bg-blue-50 rounded-xl text-sm text-blue-600 font-medium flex items-center gap-2">
              <TbClock /> Arrivée le {fmtLong(r.checkInDate)}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 flex flex-col gap-2 border-t border-gray-50 pt-4">
          {/* 🆕 VERSEMENT — action principale quand un règlement est attendu */}
          {awaitingDeposit && (
            <button
              onClick={() => onDeposit(r)}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-[var(--k-green)] text-white hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              <TbCurrencyEuro /> J'ai reçu le règlement
            </button>
          )}
          {canModify && (
            <button
              onClick={() => onModify(r)}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition flex items-center justify-center gap-2"
            >
              <TbCalendarEvent /> {isPeriodRes(r) ? "Modifier le séjour" : "Modifier les dates"}
            </button>
          )}
          {canMarkNoShow && (
            <button
              onClick={() => onNoShow(r)}
              disabled={loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition disabled:opacity-50"
            >
              Signaler une non-présentation
            </button>
          )}
          {noShowExpired && (
            <p className="text-xs text-gray-400 text-center px-2">
              Délai de signalement dépassé. Contactez le support pour une
              non-présentation constatée tardivement.
            </p>
          )}
          {r.status === "pending_cancel" && cancelReq && (
            <button
              onClick={() => onCancelApprove(cancelReq._id)}
              disabled={loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <TbCheck /> {loading ? "..." : "Confirmer l'annulation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────
export default function Reservations() {
  const { hotelId } = useParams();
  const [view, setView] = useState("table");
  const [selected, setSelected] = useState(null);
  const [modifying, setModifying] = useState(null);      // ✅ MODIFICATION
  const [noShowTarget, setNoShowTarget] = useState(null); // ✅ NO-SHOW
  const [depositTarget, setDepositTarget] = useState(null); // 🆕 VERSEMENT
  const [loading, setLoading] = useState(false);
  const [cancelRequests, setCancelRequests] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("checkInDate");
  const [sortDir, setSortDir] = useState("desc");
  const [hotelStats, setHotelStats] = useState(null);
  const [listFilter, setListFilter] = useState("all");

  const { data = [], loading: dataLoading, refreshData } = useFetch(
    `${API}/reservation/hotel/${hotelId}`
  );

  useEffect(() => {
    const m = currentDate.getMonth();
    const y = currentDate.getFullYear();
    axios.get(
      `${API}/reservation/hotel/${hotelId}/stats?month=${m}&year=${y}`,
      { withCredentials: true }
    ).then(r => setHotelStats(r.data)).catch(console.error);
  }, [hotelId, currentDate]);

  useEffect(() => {
    axios.get(
      `${API}/reservation/hotel/${hotelId}/pending-cancellations`,
      { withCredentials: true }
    ).then(r => setCancelRequests(r.data)).catch(console.error);
  }, [hotelId]);

  // ── Actions ───────────────────────────────────────────────────
  // ✅ NO-SHOW — ouvre le dialogue ; l'appel API se fait dans NoShowDialog,
  //    qui a besoin du montant retenu pour calculer la commission.
  const handleOpenNoShow = (r) => {
    setNoShowTarget(r);
    setSelected(null);
  };

  const handleNoShowDone = () => {
    setNoShowTarget(null);
    refreshData();
  };

  // 🆕 VERSEMENT
  const handleOpenDeposit = (r) => {
    setDepositTarget(r);
    setSelected(null);
  };

  const handleDepositDone = () => {
    setDepositTarget(null);
    refreshData();
  };

  const handleCancelApprove = async (reqId) => {
    try {
      setLoading(true);
      await axios.put(
        `${API}/reservation/cancellation-requests/${reqId}`,
        { status: "approved" }, { withCredentials: true }
      );
      setSelected(null); refreshData();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ✅ MODIFICATION — ouvre le drawer, ferme la fiche pour éviter deux couches
  const handleOpenModify = (r) => {
    setModifying(r);
    setSelected(null);
  };

  const handleModifyDone = () => {
    setModifying(null);
    refreshData();
  };

  // ── Stats ─────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const today = normalize(new Date());
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const monthData = data.filter(r => {
      const s = normalize(r.checkInDate);
      const e = normalize(r.checkOutDate);
      return s <= new Date(year, month + 1, 0) && e >= new Date(year, month, 1);
    });
    return {
      monthTotal:       monthData.length,
      confirmed:        data.filter(r => r.status === "confirmed" && normalize(r.checkOutDate) >= today).length,
      completed:        data.filter(r => r.status === "completed").length,
      pending_cancel:   data.filter(r => r.status === "pending_cancel").length,
      cancelled:        data.filter(r => r.status === "cancelled").length,
      no_show:          data.filter(r => r.status === "no_show").length,
      // 🆕 VERSEMENT
      awaiting_deposit: data.filter(r => r.status === "awaiting_deposit").length,
    };
  }, [data, currentDate]);

  // 🆕 VERSEMENT — règlements en attente, la plus proche échéance d'abord
  const awaitingList = useMemo(() =>
    data
      .filter(r => r.status === "awaiting_deposit")
      .sort((a, b) => new Date(a.deposit?.dueAt || 0) - new Date(b.deposit?.dueAt || 0)),
  [data]);

  // ── Calendrier ────────────────────────────────────────────────
  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
  const monthName = currentDate.toLocaleString("fr-FR", { month: "long", year: "numeric" });

  const monthReservations = useMemo(() => data.filter(r => {
    const s = normalize(r.checkInDate);
    const e = normalize(r.checkOutDate);
    return s <= new Date(year, month + 1, 0) && e >= new Date(year, month, 1);
  }), [data, year, month]);

  // ✅ Exclure cancelled et pending_cancel du calendrier.
  // 🆕 VERSEMENT — awaiting_deposit reste affiché : ses nuits SONT bloquées,
  // les masquer laisserait croire la chambre libre.
  const getDayRes = (date) => {
    const d = normalize(date);
    return monthReservations.filter(r =>
      d >= normalize(r.checkInDate) &&
      d < normalize(r.checkOutDate) &&
      !["cancelled", "pending_cancel"].includes(r.status)
    );
  };

  const uniqueRooms = useMemo(() => {
    const m = new Map();
    monthReservations
      .filter(r => !["cancelled", "pending_cancel"].includes(r.status))
      .forEach(r => { if (r.roomId?.title) m.set(r.roomId.title, true); });
    return Array.from(m.keys());
  }, [monthReservations]);

  // ── Table ─────────────────────────────────────────────────────
  const toggleSort = (f) => {
    if (sortField === f) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(f); setSortDir("asc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <TbSelector className="text-gray-300 ml-1 inline" />;
    return sortDir === "asc"
      ? <TbChevronUp className="text-gray-500 ml-1 inline" />
      : <TbChevronDown className="text-gray-500 ml-1 inline" />;
  };

  // 🆕 VERSEMENT — ordre de priorité des lignes qui demandent une action.
  // Un règlement passe avant une annulation : lui seul expire tout seul.
  const actionRank = (r) =>
    r.status === "awaiting_deposit" ? 0 : r.status === "pending_cancel" ? 1 : 2;

  const tableData = useMemo(() => {
    const today = normalize(new Date());
    let filtered = data.filter(r => {
      const text = search.toLowerCase();
      const matchSearch =
        r.guest?.firstName?.toLowerCase().includes(text) ||
        r.guest?.lastName?.toLowerCase().includes(text) ||
        r.roomId?.title?.toLowerCase().includes(text) ||
        r.reservationId?.toLowerCase().includes(text);

      let matchStatus = true;
      if (statusFilter === "confirmed")
        matchStatus = r.status === "confirmed" && normalize(r.checkOutDate) >= today;
      else if (statusFilter === "cancelled")
        // ✅ "Annulées" regroupe cancelled + pending_cancel
        matchStatus = ["cancelled", "pending_cancel"].includes(r.status);
      else if (statusFilter !== "all")
        matchStatus = r.status === statusFilter;

      return matchSearch && matchStatus;
    });

    filtered.sort((a, b) => {
      // ✅ Les lignes qui demandent une action remontent
      const ra = actionRank(a), rb = actionRank(b);
      if (ra !== rb) return ra - rb;

      let vA, vB;
      if (sortField === "guest")       { vA = a.guest?.lastName||""; vB = b.guest?.lastName||""; }
      if (sortField === "checkInDate") { vA = new Date(a.checkInDate); vB = new Date(b.checkInDate); }
      if (sortField === "price")       { vA = a.totalPrice||0; vB = b.totalPrice||0; }
      if (sortField === "room")        { vA = a.roomId?.title||""; vB = b.roomId?.title||""; }
      if (!vA && !vB) return 0;
      return vA < vB ? (sortDir==="asc"?-1:1) : vA > vB ? (sortDir==="asc"?1:-1) : 0;
    });

    return filtered;
  }, [data, search, statusFilter, sortField, sortDir]);

  // ✅ Liste — actions en premier, puis par date
  const listData = useMemo(() => {
    const filtered = listFilter === "all"
      ? data
      : listFilter === "cancelled"
        ? data.filter(r => ["cancelled", "pending_cancel"].includes(r.status))
        : data.filter(r => r.status === listFilter);

    return filtered.sort((a, b) => {
      const ra = actionRank(a), rb = actionRank(b);
      if (ra !== rb) return ra - rb;
      return new Date(b.checkInDate) - new Date(a.checkInDate);
    });
  }, [data, listFilter]);

  // ✅ Filtres — "Annulées" regroupe cancelled + pending_cancel
  const tableFilters = [
    { key: "all",       label: "Toutes",    count: data.length },
    // 🆕 VERSEMENT — filtre dédié, seulement s'il y en a
    ...(stats.awaiting_deposit > 0
      ? [{ key: "awaiting_deposit", label: "Règlements", count: stats.awaiting_deposit }]
      : []),
    { key: "confirmed", label: "En cours",  count: stats.confirmed },
    { key: "completed", label: "Terminées", count: stats.completed },
    { key: "cancelled", label: "Annulées",  count: stats.cancelled + stats.pending_cancel },
    { key: "no_show",   label: "No-show",   count: stats.no_show },
  ];

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[var(--k-green)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ───────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Réservations</h1>
          <p className="text-sm text-gray-400 mt-1">Calendrier, liste et gestion des séjours</p>
        </div>
        <div className="text-right bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm">
          <p className="text-xs text-gray-400 mb-1">Revenus bruts</p>
          <p className="text-xl font-bold text-gray-900">
            {hotelStats ? hotelStats.revenuBrut.toLocaleString("fr-FR") : "…"} €
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Commission due : <span className="text-orange-500 font-semibold">
              {hotelStats ? hotelStats.commissionDue.toLocaleString("fr-FR") : "…"} €
            </span>
          </p>
        </div>
      </div>

      {/* Toggle vue */}
      <div className="flex items-center gap-1 bg-white border border-gray-100 shadow-sm p-1 rounded-xl w-fit">
        {[
          { key: "table",    icon: <TbTable />,    label: "Tableau" },
          { key: "list",     icon: <TbList />,     label: "Liste" },
          { key: "calendar", icon: <TbCalendar />, label: "Calendrier" },
        ].map(v => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
              view === v.key ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {v.icon} {v.label}
          </button>
        ))}
      </div>

      {/* ── 🆕 VERSEMENT — règlements à valider ──────────────────
          En tête des alertes : c'est la seule situation où ne rien faire
          fait perdre la réservation. */}
      {awaitingList.length > 0 && (
        <div className="bg-white border border-red-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex items-center gap-3">
            <TbCurrencyEuro className="text-red-500 text-lg shrink-0" />
            <p className="text-sm text-red-700 font-semibold">
              {awaitingList.length} règlement{awaitingList.length > 1 ? "s" : ""} à valider
            </p>
            <button
              onClick={() => { setView("table"); setStatusFilter("awaiting_deposit"); }}
              className="ml-auto text-xs font-semibold text-red-600 hover:text-red-800 flex items-center gap-1 shrink-0"
            >
              Tout voir <TbArrowRight />
            </button>
          </div>
          <div className="px-4 py-2">
            {awaitingList.slice(0, 3).map(r => {
              const urgent = r.deposit?.dueAt && new Date(r.deposit.dueAt) <= new Date(Date.now() + 24 * 3600000);
              return (
                <div key={r._id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {r.guest?.firstName} {r.guest?.lastName}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {r.roomId?.title} · {stayLabel(r)} · dès le {fmtShort(r.checkInDate)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-gray-900">
                      {r.deposit?.amount?.toLocaleString("fr-FR")} €
                    </p>
                    <p className={`text-[11px] font-medium ${urgent ? "text-red-500" : "text-gray-400"}`}>
                      {r.deposit?.dueAt ? `avant ${fmtDeadline(r.deposit.dueAt)}` : "—"}
                    </p>
                  </div>
                  <button
                    onClick={() => handleOpenDeposit(r)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--k-green)] text-white hover:opacity-90 transition shrink-0"
                  >
                    Valider
                  </button>
                </div>
              );
            })}
            {awaitingList.length > 3 && (
              <p className="text-xs text-gray-400 py-2">
                et {awaitingList.length - 3} autre{awaitingList.length - 3 > 1 ? "s" : ""}…
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── ALERTE ANNULATIONS — toujours visible si pending_cancel ── */}
      {stats.pending_cancel > 0 && (
        <div className="px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-3">
          <TbAlertTriangle className="text-orange-500 text-lg shrink-0" />
          <p className="text-sm text-orange-700 font-medium">
            {stats.pending_cancel} demande{stats.pending_cancel > 1 ? "s" : ""} d'annulation en attente de traitement
          </p>
          <button
            onClick={() => { setView("table"); setStatusFilter("cancelled"); }}
            className="ml-auto text-xs font-semibold text-orange-600 hover:text-orange-800 flex items-center gap-1 shrink-0"
          >
            Traiter <TbArrowRight />
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          VUE CALENDRIER
      ══════════════════════════════════════════════════════ */}
      {view === "calendar" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Ce mois",      value: stats.monthTotal,    color: "text-gray-900" },
            { label: "En cours",     value: stats.confirmed,     color: "text-emerald-600" },
            { label: "Terminées",    value: stats.completed,     color: "text-slate-500" },
            {
              label: "Revenus mois",
              value: hotelStats ? `${hotelStats.monthRevenuBrut.toLocaleString("fr-FR")} €` : "…",
              sub: hotelStats ? `Commission : ${hotelStats.monthCommissionDue.toLocaleString("fr-FR")} €` : "",
              color: "text-[var(--k-green)]"
            },
          ].map((k, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <p className="text-xs text-gray-400 mb-1">{k.label}</p>
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
              {k.sub && <p className="text-xs text-orange-500 font-medium mt-1">{k.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {view === "calendar" && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition"
              >
                <TbChevronLeft className="text-gray-600" />
              </button>
              <h2 className="text-base font-bold text-gray-900 min-w-[160px] text-center capitalize">
                {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
              </h2>
              <button
                onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition"
              >
                <TbChevronRight className="text-gray-600" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {uniqueRooms.map(title => {
                const c = roomColor(title);
                return (
                  <span key={title} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.bar}`} /> {title}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-100">
              {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => (
                <div key={d} className="py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: offset }).map((_, i) => (
                <div key={`e-${i}`} className="min-h-[110px] border-b border-r border-gray-50 bg-gray-50/30" />
              ))}
              {days.map((day, i) => {
                const isToday = normalize(day).getTime() === normalize(new Date()).getTime();
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const dayRes = getDayRes(day);
                const isLastCol = (offset + i + 1) % 7 === 0;
                const isPast = normalize(day) < normalize(new Date());
                return (
                  <div
                    key={i}
                    className={`min-h-[110px] p-2 border-b border-r border-gray-50 transition-colors
                      ${isLastCol ? "border-r-0" : ""}
                      ${isWeekend ? "bg-gray-50/40" : "bg-white"}
                      ${isPast && !isToday ? "opacity-60" : ""}
                    `}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold mb-1.5
                      ${isToday ? "bg-[var(--k-green)] text-white" : "text-gray-600"}
                    `}>
                      {day.getDate()}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {dayRes.slice(0, 3).map((r, ri) => {
                        const c = roomColor(r.roomId?.title);
                        const isStart = normalize(day).getTime() === normalize(r.checkInDate).getTime();
                        const isEnd = normalize(day).getTime() === normalize(new Date(new Date(r.checkOutDate).getTime() - 86400000)).getTime();
                        // 🆕 VERSEMENT — une résa non réglée se distingue :
                        // elle occupe le calendrier mais peut encore sauter.
                        const pendingPay = r.status === "awaiting_deposit";
                        return (
                          <div
                            key={ri}
                            onClick={() => setSelected(r)}
                            className={`px-1.5 py-0.5 text-xs font-medium cursor-pointer truncate transition
                              ${pendingPay ? "bg-red-50 text-red-600 border border-dashed border-red-300" : `${c.bg} ${c.text}`}
                              ${isStart ? "rounded-l-md" : ""}
                              ${isEnd ? "rounded-r-md" : ""}
                              hover:opacity-75
                            `}
                          >
                            {isStart ? `${r.roomId?.title || "Chambre"} · ${r.guest?.firstName || ""}` : <span className="opacity-0">·</span>}
                          </div>
                        );
                      })}
                      {dayRes.length > 3 && (
                        <p className="text-xs text-gray-400 pl-1 mt-0.5">+{dayRes.length - 3}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          VUE LISTE
      ══════════════════════════════════════════════════════ */}
      {view === "list" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {[
              { key: "all",       label: "Toutes",    count: data.length },
              ...(stats.awaiting_deposit > 0
                ? [{ key: "awaiting_deposit", label: "Règlements", count: stats.awaiting_deposit }]
                : []),
              { key: "confirmed", label: "Confirmées", count: stats.confirmed },
              { key: "completed", label: "Terminées",  count: stats.completed },
              { key: "cancelled", label: "Annulées",   count: stats.cancelled + stats.pending_cancel },
              { key: "no_show",   label: "No-show",    count: stats.no_show },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setListFilter(f.key)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
                  listFilter === f.key ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {f.label} <span className="opacity-50 ml-0.5">({f.count})</span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            {listData.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 text-sm">
                Aucune réservation
              </div>
            )}
            {listData.map(r => {
              const today = normalize(new Date());
              const isOngoing  = today >= normalize(r.checkInDate) && today < normalize(r.checkOutDate);
              const isUpcoming = today < normalize(r.checkInDate);
              const c = roomColor(r.roomId?.title);
              const guestsLabel = formatGuests(r.guests);
              const awaiting = r.status === "awaiting_deposit";   // 🆕 VERSEMENT

              return (
                <div
                  key={r._id}
                  onClick={() => setSelected(r)}
                  className={`bg-white rounded-2xl border p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all
                    ${awaiting ? "border-red-200 bg-red-50/30"
                      : r.status === "pending_cancel" ? "border-orange-200 bg-orange-50/30"
                      : "border-gray-100"}
                  `}
                >
                  <div className={`w-1 self-stretch rounded-full ${
                    awaiting ? "bg-red-400" : r.status === "pending_cancel" ? "bg-orange-400" : c.bar
                  }`} />

                  <div className="text-center min-w-[52px]">
                    <p className="text-lg font-bold text-gray-900">{new Date(r.checkInDate).getDate()}</p>
                    <p className="text-xs text-gray-400">{new Date(r.checkInDate).toLocaleString("fr-FR", { month: "short" })}</p>
                  </div>
                  <TbArrowNarrowRight className="text-gray-300 shrink-0" />
                  <div className="text-center min-w-[52px]">
                    <p className="text-lg font-bold text-gray-900">{new Date(r.checkOutDate).getDate()}</p>
                    <p className="text-xs text-gray-400">{new Date(r.checkOutDate).toLocaleString("fr-FR", { month: "short" })}</p>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{r.guest?.firstName} {r.guest?.lastName}</p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {/* 🆕 PÉRIODES — durée dans l'unité vendue */}
                      {r.roomId?.title} · {stayLabel(r)}
                      {guestsLabel && <span className="ml-1">· {guestsLabel}</span>}
                      {r.modificationCount > 0 && <span className="ml-1">· modifiée</span>}
                    </p>
                  </div>

                  {/* 🆕 VERSEMENT — action directe depuis la liste */}
                  {awaiting && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleOpenDeposit(r); }}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--k-green)] text-white hover:opacity-90 transition shrink-0"
                    >
                      Valider le règlement
                    </button>
                  )}
                  {isOngoing && !awaiting && <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 font-medium shrink-0">En cours</span>}
                  {isUpcoming && !awaiting && r.status !== "pending_cancel" && <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600 font-medium shrink-0">À venir</span>}
                  {r.status === "pending_cancel" && (
                    <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-600 font-semibold shrink-0 flex items-center gap-1">
                      <TbAlertTriangle className="text-xs" /> À traiter
                    </span>
                  )}

                  <StatusBadge status={r.status} />

                  <div className="text-right shrink-0">
                    <p className="font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
                    {r.totalPriceKMF && (
                      <p className="text-[10px] text-gray-400">{r.totalPriceKMF?.toLocaleString("fr-FR")} KMF</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          VUE TABLEAU
      ══════════════════════════════════════════════════════ */}
      {view === "table" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1.5">
              {tableFilters.map(f => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                    statusFilter === f.key
                      ? "bg-gray-900 text-white shadow-sm"
                      : f.key === "awaiting_deposit"
                        ? "bg-red-50 border border-red-200 text-red-600 hover:border-red-300"
                        : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {f.label} <span className="ml-1 opacity-50">({f.count})</span>
                </button>
              ))}
            </div>
            <div className="relative">
              <TbSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Client, chambre, référence..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition bg-white w-64"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    {[
                      { label: "Référence", field: null },
                      { label: "Client",    field: "guest" },
                      { label: "Dates",     field: "checkInDate" },
                      { label: "Durée",     field: null },
                      { label: "Chambre",   field: "room" },
                      { label: "Voyageurs", field: null },
                      { label: "Total",     field: "price" },
                      { label: "Statut",    field: null },
                      { label: "",          field: null },
                    ].map((col, i) => (
                      <th
                        key={i}
                        onClick={() => col.field && toggleSort(col.field)}
                        className={`text-left px-4 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wider ${col.field ? "cursor-pointer hover:text-gray-700 select-none" : ""}`}
                      >
                        {col.label}{col.field && <SortIcon field={col.field} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-16 text-gray-400 text-sm">Aucune réservation trouvée</td></tr>
                  )}
                  {tableData.map(r => {
                    const today = normalize(new Date());
                    const isUpcoming = today < normalize(r.checkInDate);
                    const isOngoing  = today >= normalize(r.checkInDate) && today < normalize(r.checkOutDate);
                    const isPast     = today >= normalize(r.checkOutDate);
                    const cancelReq  = cancelRequests?.find(cr => String(cr.reservationId?._id || cr.reservationId) === String(r._id));
                    const guestsLabel = formatGuests(r.guests);
                    const isPendingCancel = r.status === "pending_cancel";
                    const awaiting = r.status === "awaiting_deposit";   // 🆕 VERSEMENT

                    return (
                      <tr
                        key={r._id}
                        onClick={() => setSelected(r)}
                        className={`border-b border-gray-50 last:border-0 cursor-pointer transition-colors
                          ${awaiting ? "bg-red-50/40 hover:bg-red-50/60"
                            : isPendingCancel ? "bg-orange-50/40 hover:bg-orange-50/60"
                            : r.status === "cancelled" ? "opacity-50 hover:bg-gray-50/70"
                            : "hover:bg-gray-50/70"}
                        `}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                              {r.reservationId || r._id.slice(0, 8)}
                            </span>
                            {r.source === "admin" && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-purple-50 text-purple-500 font-semibold">Admin</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-gray-900">{r.guest?.firstName} {r.guest?.lastName}</p>
                          {r.guest?.email && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[160px]">{r.guest.email}</p>}
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-gray-800 font-medium">{fmtShort(r.checkInDate)} → {fmtShort(r.checkOutDate)}</p>
                          {["confirmed","completed"].includes(r.status) && (
                            <p className={`text-xs mt-0.5 font-medium ${isUpcoming ? "text-blue-400" : isOngoing ? "text-emerald-500" : "text-gray-400"}`}>
                              {isUpcoming && "À venir"}{isOngoing && "● En cours"}{isPast && "Terminé"}
                            </p>
                          )}
                          {/* 🆕 VERSEMENT — échéance visible dans le tableau */}
                          {awaiting && r.deposit?.dueAt && (
                            <p className="text-xs mt-0.5 font-medium text-red-500">
                              {r.deposit.amount?.toLocaleString("fr-FR")} € avant {fmtShort(r.deposit.dueAt)}
                            </p>
                          )}
                          {/* ✅ MODIFICATION — trace visible dans le tableau */}
                          {r.modificationCount > 0 && (
                            <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                              <TbCalendarEvent className="text-[11px]" />
                              Dates modifiées
                            </p>
                          )}
                        </td>
                        {/* 🆕 PÉRIODES — durée dans l'unité vendue */}
                        <td className="px-4 py-4 text-gray-500 text-sm whitespace-nowrap">{stayLabel(r)}</td>
                        <td className="px-4 py-4 text-gray-700 font-medium">{r.roomId?.title || "—"}</td>
                        <td className="px-4 py-4">
                          {guestsLabel ? (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                              <TbUsers className="shrink-0" /> {guestsLabel}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
                          {r.totalPriceKMF && (
                            <p className="text-[10px] text-gray-400">{r.totalPriceKMF?.toLocaleString("fr-FR")} KMF</p>
                          )}
                        </td>
                        <td className="px-4 py-4"><StatusBadge status={r.status} /></td>
                        <td className="px-4 py-4">
                          {/* 🆕 VERSEMENT — action directe, sans ouvrir la fiche */}
                          {awaiting && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleOpenDeposit(r); }}
                              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-[var(--k-green)] text-white hover:opacity-90 transition whitespace-nowrap"
                            >
                              Valider
                            </button>
                          )}
                          {isPendingCancel && cancelReq && (
                            <span className="text-xs font-semibold text-orange-500 flex items-center gap-1">
                              <TbAlertTriangle /> À traiter
                            </span>
                          )}
                          {canReportNoShow(r) && (
                            <span className="text-xs text-gray-400">No-show ?</span>
                          )}
                          {!["pending_cancel","cancelled","no_show","awaiting_deposit"].includes(r.status) && !isPast && (
                            <TbEye className="text-gray-300 hover:text-gray-500 transition" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {tableData.length > 0 && (
              <div className="px-5 py-3 border-t border-gray-50">
                <p className="text-xs text-gray-400">{tableData.length} résultat{tableData.length > 1 ? "s" : ""}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL ────────────────────────────────────────────── */}
      {selected && (
        <ReservationModal
          r={selected}
          onClose={() => setSelected(null)}
          onNoShow={handleOpenNoShow}
          onCancelApprove={handleCancelApprove}
          onModify={handleOpenModify}
          onDeposit={handleOpenDeposit}
          cancelReq={cancelRequests?.find(cr => String(cr.reservationId?._id || cr.reservationId) === String(selected._id))}
          loading={loading}
        />
      )}

      {/* ── DIALOGUE NO-SHOW ─────────────────────────────────── */}
      {noShowTarget && (
        <NoShowDialog
          reservation={noShowTarget}
          onClose={() => setNoShowTarget(null)}
          onDone={handleNoShowDone}
        />
      )}

      {/* ── 🆕 DIALOGUE VERSEMENT ────────────────────────────── */}
      {depositTarget && (
        <DepositDialog
          reservation={depositTarget}
          onClose={() => setDepositTarget(null)}
          onDone={handleDepositDone}
        />
      )}

      {/* ── DRAWER MODIFICATION ──────────────────────────────── */}
      {modifying && (
        <ModificationDrawer
          reservation={modifying}
          onClose={() => setModifying(null)}
          onDone={handleModifyDone}
        />
      )}
    </div>
  );
}